// src/services/classComments.js
// Firestore helpers for post-class comments (class guestbook)
// Collection: classComments
// Doc ID: auto-generated
// Schema: { classId, recordingId, parentCommentId, userId, userName, userInitials, isCoach, text, createdAt, status, editedAt? }
//   editedAt: ISO timestamp, present only after an author edits their comment (additive — absent on all legacy docs)
//   parentCommentId: null for top-level comments, <comment-id> for replies (admin-only, one level deep)
// commentCount is maintained on liveClasses / classRecordings docs via atomic increment
// Replies count toward commentCount the same as top-level comments — every active comment
// contributes 1 to the aggregated total.

import { db, firebaseEnabled } from './firebase';
import {
    collection, getDocs, doc, getDoc, query, where, orderBy, limit as firestoreLimit, startAfter, increment, onSnapshot, documentId,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, updateDoc,
} from './firestoreWrite';

// ── Post a comment (or admin reply) ──
// parentCommentId is optional. When set, this comment is a reply to that parent
// (admin-only by product spec — see ClassComments.js for the client-side gate).
// Backward compatible: callers that don't pass parentCommentId get null (top-level).
export async function postComment({ userId, userName, userInitials, isCoach, classId, recordingId, text, parentCommentId }) {
    if (!firebaseEnabled || !db || !userId || !text?.trim()) return null;
    try {
        const comment = {
            classId: classId || null,
            recordingId: recordingId || null,
            parentCommentId: parentCommentId || null,
            userId,
            userName,
            userInitials,
            isCoach: isCoach || false,
            text: text.trim().slice(0, 280),
            createdAt: new Date().toISOString(),
            status: 'active',
        };
        const docRef = await addDoc(collection(db, 'classComments'), comment);

        // Increment commentCount on parent doc(s) — fired in parallel for lower
        // end-to-end post latency. Each promise has its own .catch so a missing
        // sibling doc (e.g. classRecordings not yet created) doesn't take down
        // the other write. Non-critical: liveClasses remains authoritative if
        // either increment fails to land.
        const incrementPromises = [];
        if (classId) {
            incrementPromises.push(
                updateDoc(doc(db, 'liveClasses', classId), { commentCount: increment(1) })
                    .catch(() => { /* parent doc may not exist */ })
            );
        }
        if (recordingId) {
            incrementPromises.push(
                updateDoc(doc(db, 'classRecordings', recordingId), { commentCount: increment(1) })
                    .catch(() => { /* parent doc may not exist */ })
            );
        }
        if (incrementPromises.length > 0) {
            await Promise.all(incrementPromises);
        }

        // Dual-write fallback for comments posted during the live/drain window with
        // only classId: if the VOD has already been created (vodAssetId set via D-059),
        // mirror the commentCount to the classRecordings doc so On Demand reflects
        // the true post-class thread size. Skipped when recordingId was provided
        // (caller already updated both docs above). Fire-and-forget.
        try {
            if (classId && !recordingId) {
                const classSnap = await getDoc(doc(db, 'liveClasses', classId));
                const vodAssetId = classSnap.exists() ? classSnap.data().vodAssetId : null;
                if (vodAssetId) {
                    updateDoc(doc(db, 'classRecordings', vodAssetId), { commentCount: increment(1) }).catch(() => {});
                }
            }
        } catch (e) { /* best-effort sync — liveClasses remains authoritative */ }

        return { id: docRef.id, ...comment };
    } catch (e) {
        console.error('Error posting comment:', e);
        return null;
    }
}

// ── Edit a comment's text (author-only) ──
// Mutates ONLY `text` (re-trimmed + re-capped at 280) and stamps `editedAt`.
// Deliberately does NOT touch commentCount, createdAt, parentCommentId, status,
// or any identity/threading field — so ordering, threading, and the client-side
// backfill/preservation reconciliation (COMMENTS-001/002) are entirely unaffected;
// the existing onSnapshot delivers the new text to every viewer automatically.
// Authorship is gated client-side (caller passes only the user's own comments);
// the classComments rules are open `write: if true`, identical to post/delete,
// so this introduces no new server-side authorization surface.
export async function editComment(commentId, newText) {
    if (!firebaseEnabled || !db || !commentId || !newText?.trim()) return false;
    try {
        await updateDoc(doc(db, 'classComments', commentId), {
            text: newText.trim().slice(0, 280),
            editedAt: new Date().toISOString(),
        });
        return true;
    } catch (e) {
        console.error('Error editing comment:', e);
        return false;
    }
}

// ── Fetch comments for a class or recording (paginated, newest first) ──
export async function getComments({ classId, recordingId, pageSize = 20, lastDoc = null }) {
    if (!firebaseEnabled || !db) return { comments: [], lastDoc: null, hasMore: false };
    try {
        // Build query — prefer classId when available so post-class and on-demand
        // comments are unified (post-class comments only have classId, not recordingId)
        const targetField = classId ? 'classId' : 'recordingId';
        const targetValue = classId || recordingId;
        if (!targetValue) return { comments: [], lastDoc: null, hasMore: false };

        let q;
        if (lastDoc) {
            q = query(
                collection(db, 'classComments'),
                where(targetField, '==', targetValue),
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc'),
                startAfter(lastDoc),
                firestoreLimit(pageSize)
            );
        } else {
            q = query(
                collection(db, 'classComments'),
                where(targetField, '==', targetValue),
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc'),
                firestoreLimit(pageSize)
            );
        }

        const snap = await getDocs(q);
        const comments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const newLastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        const hasMore = snap.docs.length === pageSize;

        return { comments, lastDoc: newLastDoc, hasMore };
    } catch (e) {
        console.error('Error fetching comments:', e);
        return { comments: [], lastDoc: null, hasMore: false };
    }
}

// ── Fetch a specific set of comments by document id (active only, batched) ──
// Used by ClassComments to backfill "missing parents" — replies whose parent comment
// fell outside the newest-N subscription window. Without this, an admin who replies to
// older comments creates orphaned replies on the client because the snapshot only carries
// the most recent N documents (replies are recent; their parents are old).
//
// Firestore's 'in' clause supports up to 30 values per query, so we chunk requests.
// Status is filtered client-side to keep the query on the implicit __name__ index
// (no new composite index needed). Soft-deleted parents are intentionally excluded —
// the client's existing orphan-fallback rendering catches their replies.
//
// Best-effort: per-chunk failures don't abort the whole call — we return whatever
// resolved successfully so partial backfill is better than total failure.
export async function getCommentsByIds(ids) {
    if (!firebaseEnabled || !db || !Array.isArray(ids) || ids.length === 0) return [];
    try {
        const unique = Array.from(new Set(ids.filter(Boolean)));
        if (unique.length === 0) return [];
        const chunks = [];
        const CHUNK_SIZE = 30;
        for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
            chunks.push(unique.slice(i, i + CHUNK_SIZE));
        }
        const results = [];
        for (const chunk of chunks) {
            try {
                const q = query(
                    collection(db, 'classComments'),
                    where(documentId(), 'in', chunk),
                );
                const snap = await getDocs(q);
                snap.docs.forEach((d) => {
                    const data = d.data();
                    if (data.status === 'active') {
                        results.push({ id: d.id, ...data });
                    }
                });
            } catch (chunkErr) {
                console.warn('getCommentsByIds chunk failed:', chunkErr.message);
            }
        }
        return results;
    } catch (e) {
        console.error('Error fetching comments by ids:', e);
        return [];
    }
}

// ── Subscribe to comments in real-time (newest first) ──
// Drives the live thread AND the collapsed-state preview: a single onSnapshot feeds both
// so users see each other's comments appear instantly, and the preview line under a
// collapsed comment thread always reflects the latest post without needing to expand.
// Pagination for older comments still uses getComments() with startAfter — the subscription
// covers only the newest `pageSize` (default 20). Returns an unsubscribe function.
export function subscribeToComments({ classId, recordingId, pageSize = 20, onUpdate }) {
    if (!firebaseEnabled || !db || typeof onUpdate !== 'function') return () => {};
    const targetField = classId ? 'classId' : 'recordingId';
    const targetValue = classId || recordingId;
    if (!targetValue) return () => {};

    const q = query(
        collection(db, 'classComments'),
        where(targetField, '==', targetValue),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageSize)
    );
    return onSnapshot(q, (snap) => {
        const comments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        const hasMore = snap.docs.length === pageSize;
        onUpdate({ comments, lastDoc, hasMore });
    }, (err) => {
        console.error('Error subscribing to comments:', err);
        // Surface an empty state so the UI can exit its loading spinner instead of hanging forever
        onUpdate({ comments: [], lastDoc: null, hasMore: false });
    });
}

// ── Soft-delete a comment (admin moderation) — cascades replies for top-level deletes ──
// When the deleted comment is a top-level (no parentCommentId), all its active replies
// are also soft-deleted in the same operation. The aggregated commentCount on the
// parent class/recording docs is decremented by the total deletions (1 + reply count)
// so the displayed count stays accurate.
//
// When the deleted comment is a reply, no cascade fires — replies have no children
// in our one-level threading model.
//
// Status filter on the cascade query is client-side to keep the read on a single-field
// index (avoids requiring a new composite index).
export async function deleteComment(commentId, classId, recordingId) {
    if (!firebaseEnabled || !db || !commentId) return false;
    try {
        // Soft-delete the target comment first
        await updateDoc(doc(db, 'classComments', commentId), { status: 'deleted' });
        let totalDeleted = 1;

        // Cascade: find active replies whose parentCommentId points at the deleted
        // comment, soft-delete each. Best-effort — if this fails, the parent is still
        // gone but replies remain orphaned (rare, recoverable via admin re-delete).
        try {
            const repliesSnap = await getDocs(query(
                collection(db, 'classComments'),
                where('parentCommentId', '==', commentId),
            ));
            const activeReplies = repliesSnap.docs.filter((d) => d.data().status === 'active');
            if (activeReplies.length > 0) {
                await Promise.all(activeReplies.map((d) =>
                    updateDoc(doc(db, 'classComments', d.id), { status: 'deleted' })
                ));
                totalDeleted += activeReplies.length;
            }
        } catch (e) {
            console.warn('Cascade delete failed (non-critical):', e.message);
        }

        // Decrement commentCount on parent doc(s) by the total deletions (parent + cascade)
        try {
            if (classId) {
                await updateDoc(doc(db, 'liveClasses', classId), { commentCount: increment(-totalDeleted) });
            }
            if (recordingId) {
                await updateDoc(doc(db, 'classRecordings', recordingId), { commentCount: increment(-totalDeleted) });
            }
        } catch (e) { /* parent doc may not exist */ }

        // Mirror the decrement on the VOD doc (if it exists) when only classId was
        // supplied — same logic as postComment's dual-write fallback. Keeps On
        // Demand commentCount in sync with admin soft-delete moderation.
        try {
            if (classId && !recordingId) {
                const classSnap = await getDoc(doc(db, 'liveClasses', classId));
                const vodAssetId = classSnap.exists() ? classSnap.data().vodAssetId : null;
                if (vodAssetId) {
                    updateDoc(doc(db, 'classRecordings', vodAssetId), { commentCount: increment(-totalDeleted) }).catch(() => {});
                }
            }
        } catch (e) { /* best-effort sync */ }

        return true;
    } catch (e) {
        console.error('Error deleting comment:', e);
        return false;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// Member replies (adr-member-replies-threads-and-moderation.md) — ADDITIVE.
// Used only by ClassCommentsV2 (behind siteConfig.memberRepliesEnabled); every
// function above is unchanged and still drives the current thread.
// ═══════════════════════════════════════════════════════════════════════════

// Top-level comments only, newest first, realtime. Needs the composite index
// classComments(<scope> ASC, status ASC, parentCommentId ASC, createdAt DESC).
// `onError` fires on failure (e.g. index not built) so the caller can fall back.
export function subscribeTopLevel({ classId, recordingId, limitCount = 20, onUpdate, onError }) {
    if (!firebaseEnabled || !db || typeof onUpdate !== 'function') return () => {};
    const targetField = classId ? 'classId' : 'recordingId';
    const targetValue = classId || recordingId;
    if (!targetValue) return () => {};
    const q = query(
        collection(db, 'classComments'),
        where(targetField, '==', targetValue),
        where('status', '==', 'active'),
        where('parentCommentId', '==', null),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limitCount)
    );
    return onSnapshot(q, (snap) => {
        onUpdate({ comments: snap.docs.map((d) => ({ id: d.id, ...d.data() })), hasMore: snap.docs.length === limitCount });
    }, (err) => { console.error('subscribeTopLevel:', err); if (onError) onError(err); });
}

// Replies for up to 30 parents (Firestore `in` limit), realtime. Single-field
// filter ⇒ automatic index; status is filtered by the caller. Fetched BY PARENT,
// never by recordingId, so replies whose recordingId differs from their parent's
// (posted from On Demand to a post-class comment) always group correctly.
export function subscribeRepliesFor(parentIds, onUpdate) {
    if (!firebaseEnabled || !db || !parentIds.length) return () => {};
    const q = query(collection(db, 'classComments'), where('parentCommentId', 'in', parentIds.slice(0, 30)));
    return onSnapshot(q, (snap) => onUpdate(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => console.error('subscribeRepliesFor:', err));
}

async function authedPost(path, body, getToken) {
    const token = typeof getToken === 'function' ? await getToken() : null;
    if (!token) return { ok: false, status: 401, data: {} };
    const resp = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        keepalive: true,
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data };
}

// Member posts (top-level or reply) go through the server: identity, screening,
// no-links rule, notifications. Returns { comment, held } or { error, status }.
export async function postMemberComment({ classId, recordingId, text, parentCommentId = null, replyToCommentId = null }, getToken) {
    try {
        const r = await authedPost('/api/post-comment', { classId: classId || null, recordingId: recordingId || null, text, parentCommentId, replyToCommentId }, getToken);
        if (!r.ok) return { error: r.data.error || 'failed', status: r.status };
        return r.data;
    } catch (e) { return { error: 'network', status: 0 }; }
}

export async function deleteOwnComment(commentId, getToken) {
    try { return (await authedPost('/api/delete-comment', { commentId }, getToken)).ok; } catch (e) { return false; }
}

export async function reportComment(commentId, reason, getToken) {
    try { return (await authedPost('/api/report-comment', { commentId, reason }, getToken)).ok; } catch (e) { return false; }
}

// Ids this member has reported (so they stay hidden for her on every device).
export async function fetchMyReportedIds(userId) {
    if (!firebaseEnabled || !db || !userId) return [];
    try {
        const snap = await getDocs(query(collection(db, 'commentReports'), where('reporterId', '==', userId)));
        return snap.docs.map((d) => d.data().commentId);
    } catch (e) { return []; }
}
